import type { Subscription } from '../subscription/entity';
import {
  SUBSCRIPTION_STATUS_PAYMENT_FAILED,
  SUBSCRIPTION_STATUS_PAST_DUE,
  SUBSCRIPTION_STATUS_PIX_MANUAL,
} from './subscription-status';

/**
 * Usuário deve ir para escolha de plano/checkout (PIX expirado, cartão em falha, etc.).
 */
export function userRequiresSubscriptionRenewal(subscription: Subscription | null): boolean {
  if (!subscription) return false;
  const st = subscription.subscription_status ?? '';

  if (st === SUBSCRIPTION_STATUS_PAST_DUE || st === SUBSCRIPTION_STATUS_PAYMENT_FAILED) {
    return subscription.plan_id != null;
  }

  if (st === SUBSCRIPTION_STATUS_PIX_MANUAL) {
    const until = subscription.subscription_valid_until;
    if (until == null || until.trim() === '') {
      return false;
    }
    return new Date(until) <= new Date();
  }

  if (st === 'authorized' && subscription.subscription_valid_until) {
    return new Date(subscription.subscription_valid_until) <= new Date();
  }

  return false;
}

/**
 * Acesso ao app além da verificação de `plan_id` (período válido / status).
 * Documentos antigos sem `subscription_valid_until` mantêm acesso se têm plano.
 */
export function userHasActivePlanAccess(subscription: Subscription | null): boolean {
  if (!subscription || subscription.plan_id == null) return false;
  const st = subscription.subscription_status ?? '';
  const stLower = st.trim().toLowerCase();

  if (stLower === 'cancelled' || stLower === 'canceled') return false;
  if (st === SUBSCRIPTION_STATUS_PAST_DUE || st === SUBSCRIPTION_STATUS_PAYMENT_FAILED) {
    return false;
  }

  const until = subscription.subscription_valid_until;
  if (until == null || until.trim() === '') {
    if (st === SUBSCRIPTION_STATUS_PIX_MANUAL) {
      return false;
    }
    /* Mercado Pago / legado */
    if (stLower === 'authorized' || stLower === 'pending') return true;
    /* Stripe: webhooks gravam active/trialing sem subscription_valid_until obrigatório */
    if (stLower === 'active' || stLower === 'trialing') return true;
    /* Documento antigo: só plan_id */
    if (st.trim() === '') return true;
    return false;
  }

  if (st === SUBSCRIPTION_STATUS_PIX_MANUAL || stLower === 'authorized') {
    return new Date(until) > new Date();
  }

  return true;
}

/**
 * Pode chamar cancelamento Stripe ao fim do período (assinatura ativa, ainda não agendada).
 */
export function userCanCancelStripeSubscriptionAtPeriodEnd(subscription: Subscription | null): boolean {
  if (!subscription) return false;
  const subId = subscription.stripe_subscription_id?.trim();
  if (!subId) return false;
  if (subscription.subscription_cancel_at_period_end) return false;
  const st = (subscription.subscription_status ?? '').trim().toLowerCase();
  return st === 'active' || st === 'trialing';
}
